import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CreditCard,
  Loader2,
  Plus,
  WalletCards,
} from "lucide-react";
import { api, formatApiError, money } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PERIODS = [
  { id: "day", label: "Gün" },
  { id: "week", label: "Hafta" },
  { id: "month", label: "Ay" },
];

const isoDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDate = (value) => new Date(`${value}T00:00:00`);

const turkeyIsoDate = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const shiftAnchor = (value, period, direction) => {
  const date = parseDate(value);
  if (period === "day") date.setDate(date.getDate() + direction);
  if (period === "week") date.setDate(date.getDate() + direction * 7);
  if (period === "month") {
    date.setDate(1);
    date.setMonth(date.getMonth() + direction);
  }
  return isoDate(date);
};

const periodTitle = (summary) => {
  if (!summary) return "Kasa";
  const start = parseDate(summary.start_date);
  const end = parseDate(summary.end_date);
  if (summary.period === "day") {
    return start.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
  }
  if (summary.period === "month") {
    return start.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  }
  return `${start.toLocaleDateString("tr-TR", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}`;
};

export default function CashRegister() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState("day");
  const [anchorDate, setAnchorDate] = useState(turkeyIsoDate());
  const [transactionType, setTransactionType] = useState(null);
  const [form, setForm] = useState({ amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const { data: summary, isLoading } = useQuery({
    queryKey: ["cash-summary", period, anchorDate],
    queryFn: () => api.get("/cash/summary", { params: { period, date: anchorDate } })
      .then((response) => response.data),
  });

  const todayInTurkey = turkeyIsoDate();
  const nextAnchor = shiftAnchor(anchorDate, period, 1);
  const isFuturePeriod = anchorDate > todayInTurkey;
  const isCurrentPeriod = useMemo(() => {
    return summary?.anchor_date === todayInTurkey && period === "day";
  }, [period, summary?.anchor_date, todayInTurkey]);

  const openTransaction = (type) => {
    setForm({ amount: "", note: "" });
    setTransactionType(type);
  };

  const saveTransaction = async () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Geçerli bir tutar girin");
      return;
    }
    setSaving(true);
    try {
      await api.post("/cash/transactions", {
        transaction_type: transactionType,
        amount,
        note: form.note,
        date: anchorDate,
      });
      queryClient.invalidateQueries({ queryKey: ["cash-summary"] });
      toast.success(transactionType === "income" ? "Para girişi eklendi" : "Para çıkışı eklendi");
      setTransactionType(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-6" data-testid="cash-register-page">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">İşletme kasası</p>
        <h1 className="mt-1 font-display text-4xl font-bold sm:text-5xl">Kasa</h1>
      </section>

      <div className="grid grid-cols-3 rounded-xl bg-secondary p-1" data-testid="cash-period-tabs">
        {PERIODS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setPeriod(item.id)}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-200",
              period === item.id ? "bg-card text-primary shadow-sm" : "text-muted-foreground",
            )}
            data-testid={`cash-period-${item.id}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between" data-testid="cash-period-navigation">
        <Button
          size="icon"
          variant="outline"
          onClick={() => setAnchorDate(shiftAnchor(anchorDate, period, -1))}
          aria-label="Önceki dönem"
          data-testid="cash-previous-period"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{isCurrentPeriod ? "Bugün" : "Seçili dönem"}</p>
          <h2 className="font-display text-xl font-bold" data-testid="cash-period-title">{periodTitle(summary)}</h2>
        </div>
        <Button
          size="icon"
          variant="outline"
          onClick={() => setAnchorDate(shiftAnchor(anchorDate, period, 1))}
          disabled={nextAnchor > todayInTurkey}
          aria-label="Sonraki dönem"
          data-testid="cash-next-period"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {isLoading || !summary ? (
        <div className="flex justify-center py-20" data-testid="cash-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <section className="border border-border bg-card p-6 text-center" data-testid="cash-net-card">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Cebe kalan</p>
            <p className="mt-2 font-display text-5xl font-bold text-primary" data-testid="cash-net-amount">
              {money(summary.net)}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {money(summary.incoming)} girdi − {money(summary.outgoing)} çıktı
            </p>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <div className="border border-border bg-card p-5" data-testid="cash-incoming-card">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Giren</p>
                <ArrowDownLeft className="h-5 w-5 text-primary" />
              </div>
              <p className="mt-3 font-display text-3xl font-bold text-primary">{money(summary.incoming)}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Nakit {money(summary.cash_total)} · Kart {money(summary.card_total)}
              </p>
            </div>
            <div className="border border-border bg-card p-5" data-testid="cash-outgoing-card">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Çıkan</p>
                <ArrowUpRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="mt-3 font-display text-3xl font-bold">{money(summary.outgoing)}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {summary.outgoing ? "Gider kaydı var" : "Gider yok"}
              </p>
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <Button
              onClick={() => openTransaction("income")}
              disabled={isFuturePeriod}
              className="h-12 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="cash-add-income"
            >
              <Plus className="mr-2 h-5 w-5" />
              Para girdi
            </Button>
            <Button
              onClick={() => openTransaction("expense")}
              disabled={isFuturePeriod}
              variant="outline"
              className="h-12"
              data-testid="cash-add-expense"
            >
              <ArrowUpRight className="mr-2 h-5 w-5" />
              Para çıktı
            </Button>
          </section>

          <section data-testid="cash-movements">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold">Hareketler</h2>
              <span className="text-sm text-muted-foreground">{summary.movement_count} kayıt</span>
            </div>
            {summary.movements.length === 0 ? (
              <div className="border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground" data-testid="cash-empty-state">
                Bu dönem için kasa hareketi yok.
              </div>
            ) : (
              <div className="divide-y divide-border border-y border-border">
                {summary.movements.map((movement) => {
                  const isIncome = movement.transaction_type === "income";
                  return (
                    <article
                      key={movement.id}
                      className="flex items-center gap-3 py-4"
                      data-testid={`cash-movement-${movement.id}`}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                        {movement.source === "reservation" ? <CreditCard className="h-5 w-5" /> : <WalletCards className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{movement.title}</p>
                        <p className="truncate text-sm text-muted-foreground">
                          {movement.time} · {movement.detail}
                        </p>
                      </div>
                      <span className={cn("font-display text-lg font-bold", isIncome ? "text-primary" : "text-foreground")}>
                        {isIncome ? "+" : "−"}{money(movement.amount)}
                      </span>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <Dialog open={Boolean(transactionType)} onOpenChange={(value) => !value && setTransactionType(null)}>
        <DialogContent data-testid="cash-transaction-dialog">
          <DialogHeader>
            <DialogTitle>{transactionType === "income" ? "Para girdi" : "Para çıktı"}</DialogTitle>
            <DialogDescription>
              {transactionType === "income" ? "Kasa girişini" : "Kasa giderini"} {anchorDate} tarihine kaydedin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((value) => ({ ...value, amount: event.target.value }))}
              placeholder="Tutar"
              data-testid="cash-transaction-amount"
            />
            <Input
              value={form.note}
              onChange={(event) => setForm((value) => ({ ...value, note: event.target.value }))}
              placeholder="Not (isteğe bağlı)"
              data-testid="cash-transaction-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransactionType(null)} data-testid="cash-transaction-cancel">
              Vazgeç
            </Button>
            <Button onClick={saveTransaction} disabled={saving} data-testid="cash-transaction-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}